import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';
import Button from './components/Button';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    // Sample cart items - in real app, this would come from state management/context
    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Abstract Expression Tee',
            price: 1299,
            size: 'M',
            color: 'Black',
            quantity: 2,
            image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=250&fit=crop'
        },
        {
            id: 2,
            name: 'Urban Nights Hoodie',
            price: 2499,
            size: 'L',
            color: 'Navy',
            quantity: 1,
            image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=200&h=250&fit=crop'
        },
        {
            id: 3,
            name: 'Nature Print Sweatshirt',
            price: 1899,
            size: 'M',
            color: 'White',
            quantity: 1,
            image: 'https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=200&h=250&fit=crop'
        },
    ]);

    const handleQuantityChange = (itemId, change) => {
        setCartItems(cartItems.map(item => {
            if (item.id === itemId) {
                const newQuantity = item.quantity + change;
                if (newQuantity >= 1 && newQuantity <= 10) {
                    return { ...item, quantity: newQuantity };
                }
            }
            return item;
        }));
    };

    const handleRemoveItem = (itemId) => {
        setCartItems(cartItems.filter(item => item.id !== itemId));
    };

    const handleApplyCoupon = () => {
        if (couponCode.toUpperCase() === 'SAVE10') {
            setAppliedCoupon({ code: 'SAVE10', discount: 10 });
        } else if (couponCode.toUpperCase() === 'FIRST20') {
            setAppliedCoupon({ code: 'FIRST20', discount: 20 });
        } else {
            alert('Invalid coupon code');
        }
    };

    // Calculate totals
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const discount = appliedCoupon ? (subtotal * appliedCoupon.discount) / 100 : 0;
    const shipping = subtotal > 999 ? 0 : 99;
    const total = subtotal - discount + shipping;

    if (cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
                <Header cartCount={0} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-4">
                        <ShoppingBag size={80} className="text-orange-300 mx-auto mb-6" />
                        <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            Your Cart is Empty
                        </h2>
                        <p className="text-gray-600 mb-8">
                            Looks like you haven't added any items to your cart yet. Start exploring our creative collections!
                        </p>
                        <Button variant="primary" size="lg" onClick={() => navigate('/template20/shop')}>
                            Continue Shopping
                        </Button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <Header showBackButton={true} cartCount={cartItems.length} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-8">
                    Shopping Cart
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cartItems.map(item => (
                            <div key={item.id} className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg hover:shadow-xl transition-shadow">
                                <div className="flex gap-4">
                                    {/* Product Image */}
                                    <div className="w-24 h-32 lg:w-32 lg:h-40 flex-shrink-0 overflow-hidden rounded-xl">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* Product Details */}
                                    <div className="flex-1 flex flex-col">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h3 className="font-semibold text-lg text-gray-900 mb-1">
                                                    {item.name}
                                                </h3>
                                                <p className="text-sm text-gray-600">
                                                    Size: {item.size} | Color: {item.color}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveItem(item.id)}
                                                className="p-2 hover:bg-red-50 rounded-full transition-colors group"
                                            >
                                                <Trash2 size={20} className="text-gray-400 group-hover:text-red-500" />
                                            </button>
                                        </div>

                                        <div className="flex justify-between items-end mt-auto">
                                            {/* Quantity Selector */}
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => handleQuantityChange(item.id, -1)}
                                                    disabled={item.quantity <= 1}
                                                    className="p-2 bg-orange-100 rounded-full hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                >
                                                    <Minus size={16} className="text-orange-600" />
                                                </button>
                                                <span className="font-semibold w-8 text-center">{item.quantity}</span>
                                                <button
                                                    onClick={() => handleQuantityChange(item.id, 1)}
                                                    disabled={item.quantity >= 10}
                                                    className="p-2 bg-orange-100 rounded-full hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                >
                                                    <Plus size={16} className="text-orange-600" />
                                                </button>
                                            </div>

                                            {/* Price */}
                                            <div className="text-right">
                                                <p className="text-xl font-bold text-gray-900">
                                                    ₹{item.price * item.quantity}
                                                </p>
                                                {item.quantity > 1 && (
                                                    <p className="text-sm text-gray-500">
                                                        ₹{item.price} each
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Continue Shopping Button */}
                        <Button
                            variant="outline"
                            fullWidth={true}
                            onClick={() => navigate('/template20/shop')}
                        >
                            Continue Shopping
                        </Button>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
                            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                Order Summary
                            </h2>

                            {/* Coupon Code */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Have a coupon code?
                                </label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Tag size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                            placeholder="SAVE10"
                                            className="w-full pl-10 pr-4 py-3 border border-orange-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                    </div>
                                    <Button variant="secondary" size="md" onClick={handleApplyCoupon}>
                                        Apply
                                    </Button>
                                </div>
                                {appliedCoupon && (
                                    <div className="mt-2 flex items-center justify-between text-sm">
                                        <span className="text-green-600 font-medium">
                                            {appliedCoupon.code} applied! ({appliedCoupon.discount}% off)
                                        </span>
                                        <button
                                            onClick={() => setAppliedCoupon(null)}
                                            className="text-red-500 hover:text-red-600"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Price Breakdown */}
                            <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                                <div className="flex justify-between text-gray-700">
                                    <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                                    <span>₹{subtotal}</span>
                                </div>
                                {appliedCoupon && (
                                    <div className="flex justify-between text-green-600 font-medium">
                                        <span>Discount ({appliedCoupon.discount}%)</span>
                                        <span>-₹{discount}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-gray-700">
                                    <span>Shipping</span>
                                    <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                                        {shipping === 0 ? 'FREE' : `₹${shipping}`}
                                    </span>
                                </div>
                                {shipping > 0 && (
                                    <p className="text-xs text-gray-500">
                                        Add ₹{999 - subtotal} more for free shipping
                                    </p>
                                )}
                            </div>

                            {/* Total */}
                            <div className="flex justify-between items-center mb-6 text-xl font-bold">
                                <span className="text-gray-900">Total</span>
                                <span className="text-2xl bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                    ₹{total}
                                </span>
                            </div>

                            {/* Checkout Button */}
                            <Button
                                variant="primary"
                                size="lg"
                                fullWidth={true}
                                onClick={() => navigate('/template20/checkout')}
                            >
                                Proceed to Checkout
                                <ArrowRight size={20} className="inline ml-2" />
                            </Button>

                            {/* Security Badge */}
                            <p className="text-xs text-center text-gray-500 mt-4">
                                Secure checkout with SSL encryption
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Cart;
